import React from 'react';
import { Wifi, Lock, Video, Zap, Droplet, Bath, Volume2, Armchair } from 'lucide-react';

const FacilitiesSection = () => {
  const facilities = [
    {
      icon: Volume2,
      title: 'Silent Study Zone',
      description: 'Completely silent environment with strict no-talking policy for maximum concentration',
      color: 'blue'
    },
    {
      icon: Armchair,
      title: 'Private Desks',
      description: 'Spacious personal desks with ergonomic chairs and ample workspace',
      color: 'sky'
    },
    {
      icon: Lock,
      title: 'Secure Lockers',
      description: 'Individual lockers provided to safely store your books and belongings',
      color: 'indigo'
    },
    {
      icon: Video,
      title: 'CCTV Cameras',
      description: '24/7 surveillance ensuring a safe and secure study environment',
      color: 'blue'
    },
    {
      icon: Zap,
      title: 'Power Backup',
      description: 'Uninterrupted power supply with backup generators for continuous studying',
      color: 'sky'
    },
    {
      icon: Wifi,
      title: 'Free Wi-Fi',
      description: 'High-speed internet connectivity for research and online learning',
      color: 'indigo'
    },
    {
      icon: Droplet,
      title: 'Filtered Water',
      description: 'Clean drinking water with RO filtration system available throughout the day',
      color: 'blue'
    },
    {
      icon: Bath,
      title: 'Clean Washrooms',
      description: 'Well-maintained and hygienic washrooms with regular cleaning',
      color: 'sky'
    }
  ];

  const colorClasses = {
    blue: {
      gradient: 'from-blue-500 to-blue-600',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200'
    },
    sky: {
      gradient: 'from-sky-500 to-sky-600',
      bg: 'bg-sky-50',
      text: 'text-sky-700',
      border: 'border-sky-200'
    },
    indigo: {
      gradient: 'from-indigo-500 to-indigo-600',
      bg: 'bg-indigo-50',
      text: 'text-indigo-700',
      border: 'border-indigo-200'
    }
  };

  return (
    <div className="py-20 bg-gradient-to-b from-white to-blue-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
            High-Class Amenities
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            Our Facilities
          </h2>
          <p className="text-lg text-gray-600">
            Everything you need for a productive study session, all under one roof
          </p>
        </div>

        {/* Facilities Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {facilities.map((facility, index) => {
            const Icon = facility.icon;
            const colors = colorClasses[facility.color];

            return (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 group"
              >
                <div className={`bg-gradient-to-br ${colors.gradient} w-16 h-16 rounded-xl flex items-center justify-center mb-4 transform group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="text-white" size={32} />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {facility.title}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed">
                  {facility.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-4 mt-16">
          <div className="bg-white rounded-2xl p-6 border-2 border-sky-200 text-center">
            <p className="text-3xl font-bold text-sky-700 mb-1">Morning Session</p>
            <p className="text-gray-600 text-sm">09:00 – 16:00</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border-2 border-blue-200 text-center">
            <p className="text-3xl font-bold text-sky-700 mb-1">Evening Session</p>
            <p className="text-gray-600 text-sm">16:00 – 22:00</p>
          </div>
        </div>

        {/* Feature Banner */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-8 text-center border-2 border-blue-200 hover:border-blue-400 transition-colors">
            <div className="text-2xl font-bold text-blue-700 mb-2">09AM-10PM</div>
            <p className="text-gray-600">Access Hours</p>
          </div>
          <div className="bg-white rounded-2xl p-8 text-center border-2 border-blue-200 hover:border-blue-400 transition-colors">
            <div className="text-2xl font-bold text-blue-700 mb-2">Essential Utilities</div>
            <p className="text-gray-600">Power Backup</p>
          </div>
          <div className="bg-white rounded-2xl p-8 text-center border-2 border-blue-200 hover:border-blue-400 transition-colors">
            <div className="text-2xl font-bold text-blue-700 mb-2">24/7</div>
            <p className="text-gray-600">CCTV Monitoring</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilitiesSection;
